import React from 'react'
import { forwardRef } from '@grupoboticario/flora-react-system'
import { Spinner } from '@grupoboticario/flora-react-spinner'
import { FloraButtonProps } from './types'
import { StyledButton, ButtonIcon } from './styles'

/**
 * @component
 * @name FloraButton
 * @description Elemento interativo para realizar ações em aplicativos e sites. Ele pode conter texto, ícones ou ambos, variar em estilo e aparência, oferecer feedback visual quando pressionado e desempenha um papel crucial na usabilidade, fornecendo uma maneira intuitiva e consistente para os usuários executarem ações específicas, como enviar formulários, salvar alterações ou navegar para outras páginas. Além disso, os botões devem ser projetados com acessibilidade em mente, para garantir que todos os usuários possam interagir com eles de forma eficaz.
 * @tutorial https://florabook.gb.tech/index.html?path=/docs/core-components-florabutton
 */
export const FloraButton = forwardRef<'button', FloraButtonProps>(
  (
    {
      'autotagging-flora': autotaggingFlora = 'off',
      'data-flora-text': dataFloraText,
      'data-autotagging-flora-section': dataAutotaggingFloraSection = '',
      'aria-label': ariaLabel,
      as = 'button',
      children,
      disabled = false,
      has = 'iconRight',
      isLoading = false,
      hierarchy = 'primary',
      icon,
      isFull = false,
      isUppercase = false,
      styleSemantic = 'neutral',
      size = 'medium',
      surfaceColor = 'light',
      ...props
    },
    forwardedRef,
  ) => {
    return (
      <StyledButton
        {...props}
        ref={forwardedRef}
        as={as}
        aria-label={ariaLabel}
        aria-busy={isLoading}
        autotagging-flora={autotaggingFlora}
        data-flora-text={dataFloraText || ariaLabel || (typeof children === 'string' && children)}
        data-autotagging-flora-section={dataAutotaggingFloraSection}
        data-flora="button"
        disabled={disabled}
        styleSemantic={styleSemantic}
        size={size}
        surfaceColor={surfaceColor}
        hierarchy={hierarchy}
        hasIconOnly={has === 'iconOnly'}
        hasIcon={!!icon}
        has={has}
        isFull={isFull}
        isUppercase={isUppercase}
        isLoading={isLoading}
      >
        {has !== 'iconOnly' && <span className="button-label">{children}</span>}

        {(icon || isLoading) && (
          <ButtonIcon data-testid={`button-${has}`} aria-hidden="true" className="button-icon">
            {isLoading ? <Spinner /> : icon}
          </ButtonIcon>
        )}
      </StyledButton>
    )
  },
)
